import type React from "react";

import { useDispatch } from "react-redux";

import { colorsPalettes } from "@similarweb/styles";
import { Button, Modal, Tooltip } from "@similarweb/ui-components";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { showSuccessToast } from "app/actions/toast_actions";
import { translate } from "app/filters/i18nFilter";
import { Textarea } from "app/pages/sales-intelligence/common-components/Textarea/Textarea";
import InfoIconTooltip from "app/pages/sales-intelligence/common-components/tooltip/InfoIconTooltip/InfoIconTooltip";
import { AIAgentCaption } from "app/pages/sales-intelligence/modules/ai-agent/components/AIAgentCaption";
import { useGetUserInfo } from "app/pages/sales-intelligence/modules/ai-agent/queries/useGetUserInfo";
import AIAgentTrackingService from "app/pages/sales-intelligence/modules/ai-agent/services/tracking/trackingService";
import {
    StyledModalContent,
    StyledModalScrollableBody,
} from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/AgentKnowledgeBaseModal/styles";
import { ChipGroup } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/ChipGroup/ChipGroup";
import { CountriesDropdown } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/CountriesDropdown/CountriesDropdown";
import {
    FILES_LIMIT,
    FileUpload,
} from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/FileUpload/FileUpload";
import { LanguageDropdown } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/LanguageDropdown/LanguageDropdown";
import { PreviousSettings } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/PreviousSettings/PreviousSettings";
import { RolesDropdown } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/RolesDropdown/RolesDropdown";
import { WithTitleContainer } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/components/WithTitleContainer/WithTitleContainer";
import { useKnowledgeBase } from "app/pages/sales-intelligence/modules/ai-agent/sub-modules/agent-knowledge-base/hooks/useKnowledgeBase";

type Props = {
    isOpen: boolean;
    onClose: () => void;
    contentRef: React.RefObject<HTMLDivElement | null>;
};

export const AgentKnowledgeBaseModal: React.FC<Props> = ({ isOpen, onClose, contentRef }) => {
    const { value, onChange, onSubmit, resetValues } = useKnowledgeBase();
    const { data: userInfo } = useGetUserInfo();
    const dispatch = useDispatch();

    const isSubmitDisabled = !value.productAndOfferings.length;

    const handleSubmit = () => {
        onSubmit();
        onClose();
        AIAgentTrackingService.kb.trackSaveChanges(value.additionalNotes);
        setTimeout(() => {
            dispatch(showSuccessToast(translate("si.agent_knowledge_base.modal.save.toast")));
        }, 1000);
    };

    const handleCancel = () => {
        onClose();
        resetValues();
        AIAgentTrackingService.kb.trackCancel();
    };

    return (
        <Modal isOpen={isOpen}>
            <StyledModalContent>
                <Flex
                    alignItems="center"
                    height="65px"
                    borderBottom={`1px solid ${colorsPalettes.carbon[100]}`}
                    padding="20px 24px"
                    gap="8px"
                    data-automation="modal-title"
                >
                    <Text fontVariant="h5_2">{translate("si.agent_knowledge_base.modal.title")}</Text>
                    <InfoIconTooltip tooltipKey="si.agent_knowledge_base.modal.title.tooltip" />
                </Flex>
                <StyledModalScrollableBody ref={contentRef}>
                    <WithTitleContainer
                        title={translate("si.agent_knowledge_base.modal.productOffering.title")}
                        dataAutomation="product-offering"
                    >
                        <ChipGroup
                            value={value.productAndOfferings}
                            onChange={(value) => onChange("productAndOfferings", value)}
                            onNewValueChange={(value) => onChange("newProductOffering", value)}
                            onAdd={(value) =>
                                AIAgentTrackingService.kb.trackProductOfferingAdd(value, userInfo?.companyName)
                            }
                            onRemove={() => AIAgentTrackingService.kb.trackProductOfferingRemove()}
                        />
                    </WithTitleContainer>
                    <WithTitleContainer
                        title={translate("si.agent_knowledge_base.modal.file.title")}
                        tooltip={translate("si.agent_knowledge_base.modal.file.tooltip")}
                        dataAutomation="file-upload"
                        additionalContent={
                            <Text
                                fontVariant="body2"
                                color={colorsPalettes.carbon[300]}
                                margin="0 0 0 auto"
                                display="block"
                            >
                                {value.files.length}/{FILES_LIMIT}
                            </Text>
                        }
                    >
                        <FileUpload
                            value={value.files}
                            productOfferings={value.productAndOfferings}
                        />
                    </WithTitleContainer>
                    <CountriesDropdown
                        value={value.targetMarketCountry}
                        onChange={(value) => onChange("targetMarketCountry", value)}
                        dataAutomation="country-dropdown"
                    />
                    <LanguageDropdown
                        value={value.language}
                        onChange={(value) => onChange("language", value)}
                        dataAutomation="language-dropdown"
                    />
                    <WithTitleContainer
                        title={translate("si.agent_knowledge_base.modal.other.title")}
                        dataAutomation="other-notes"
                    >
                        <Textarea
                            value={value.additionalNotes}
                            onChange={(value) => {
                                onChange("additionalNotes", value);
                            }}
                            placeholder={translate("si.agent_knowledge_base.modal.other.placeholder")}
                            width={502}
                        />
                    </WithTitleContainer>
                    <PreviousSettings>
                        <Box padding="38px 0 0">
                            <RolesDropdown
                                value={value.role}
                                optionValue={value.roleOption}
                                onChange={(value) => onChange("role", value)}
                                onOptionChange={(value) => onChange("roleOption", value)}
                                rolesOptions={value.roles}
                                dataAutomation="roles"
                            />
                            <WithTitleContainer
                                title={translate("si.agent_knowledge_base.modal.persona.title")}
                                dataAutomation="persona"
                            >
                                <ChipGroup
                                    onChange={(value) => onChange("targetPersonas", value)}
                                    value={value.targetPersonas}
                                    onAdd={(value) => AIAgentTrackingService.kb.trackPersonaAdd(value)}
                                    onRemove={() => AIAgentTrackingService.kb.trackPersonaRemove()}
                                />
                            </WithTitleContainer>
                            <WithTitleContainer
                                title={translate("si.agent_knowledge_base.modal.companies.title")}
                                withMargin={false}
                                dataAutomation="companies"
                            >
                                <ChipGroup
                                    onChange={(value) => onChange("targetCompanies", value)}
                                    value={value.targetCompanies}
                                    onAdd={(value) => AIAgentTrackingService.kb.trackTargetCompanyAdd(value)}
                                    onRemove={() => AIAgentTrackingService.kb.trackTargetCompanyRemove()}
                                />
                            </WithTitleContainer>
                        </Box>
                    </PreviousSettings>
                    <Box margin="36px 0 0">
                        <AIAgentCaption textId="si.agent_knowledge_base.modal.info.caption" />
                    </Box>
                </StyledModalScrollableBody>
                <Flex
                    alignItems="center"
                    justifyContent="flex-end"
                    gap="8px"
                    padding="12px 16px"
                    borderTop={`1px solid ${colorsPalettes.carbon[100]}`}
                >
                    <Button
                        type="flat"
                        onClick={handleCancel}
                        data-automation="cancel-button"
                    >
                        {translate("si.common.button.cancel")}
                    </Button>
                    <Tooltip
                        content={translate("si.agent_knowledge_base.modal.submit.disabled.tooltip")}
                        enabled={isSubmitDisabled}
                    >
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                            isDisabled={isSubmitDisabled}
                            data-automation="save-button"
                        >
                            {translate("si.common.button.save")}
                        </Button>
                    </Tooltip>
                </Flex>
            </StyledModalContent>
        </Modal>
    );
};
